import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { css } from '@emotion/css';

import { sync } from '#app/appSlice';
import { prewarmMonth } from '#components/budget/util';
import { AyudaGavi } from '#components/mobile/ayuda/AyudaGavi';
import { OjoBuey } from '#components/mobile/ayuda/OjoBuey';
import { usePreguntasInicio } from '#components/mobile/ayuda/usePreguntasInicio';
import { AvisoLlegada } from '#components/mobile/budget/travesia/AvisoLlegada';
import { useRefrescarObjetivos } from '#components/mobile/budget/useDatosObjetivos';
import { AnclaCopia } from '#components/mobile/copia/AnclaCopia';
import {
  RUTA_GAVI,
  useGaviInteligente,
} from '#components/mobile/gavi/useGaviInteligente';
import { HuecoBarra } from '#components/mobile/HuecoBarra';
import { AvisoPagaLlegada } from '#components/mobile/nomina/AvisoPagaLlegada';
import { useOpcionesPantalla } from '#components/mobile/opciones/OpcionesPantalla';
import { Boton } from '#components/mobile/ui/Boton';
import { Cargando } from '#components/mobile/ui/Cargando';
import { EstadoVacio } from '#components/mobile/ui/EstadoVacio';
import { color, espacio } from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { useTripulacion } from '#components/pareja/useTripulacion';
import { SyncRefresh } from '#components/SyncRefresh';
import { useNavigate } from '#hooks/useNavigate';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSpreadsheet } from '#hooks/useSpreadsheet';
import { useDispatch } from '#redux';

import { BotonAnadirTarjeta } from './EdicionInicio';
import { entradaEscalonada, estilos } from './inicio.estilos';
import type { IdWidget } from './modeloWidgets';
import { RejillaInicio } from './RejillaInicio';
import { SaludoCabecera } from './SaludoCabecera';
import { useEdicionTarjetas } from './useEdicionTarjetas';
import { useInicioWidgets } from './useInicioWidgets';
import { useModuloConsejos } from './widgets/Consejos';
import { COMPONENTES_WIDGET } from './widgets/registro';

const claseLista = css(entradaEscalonada);

/**
 * Pantalla de inicio de «Presupuesto» (solo móvil), personalizable como los
 * widgets del móvil: una rejilla de dos columnas con las tarjetas (Hoy, Por
 * hacer, Fijadas…) y su orden y tamaño en la preferencia sincronizada
 * `inicio-widgets`. Mantener pulsada una tarjeta (o «Personalizar Inicio»)
 * abre el modo editar ahí mismo: arrastrar, cambiar el tamaño con el asa,
 * quitar con la ✕ y añadir desde el catálogo.
 */
export function InicioPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const spreadsheet = useSpreadsheet();
  const { widgets, guardar, restablecer } = useInicioWidgets();
  const preguntasAyuda = usePreguntasInicio();
  const [ayudaAbierta, setAyudaAbierta] = useState(false);
  const navigate = useNavigate();
  // Gavi inteligente: solo el admin y en un presupuesto que es solo suyo.
  const gaviInteligente = useGaviInteligente();

  const month = monthUtils.currentMonth();
  useRefrescarObjetivos(month);
  const sheetName = monthUtils.sheetForMonth(month);

  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void prewarmMonth(spreadsheet, month).then(() => {
      if (!cancelled) {
        setInitialized(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [spreadsheet, month]);

  // Widgets que no se pintan aquí: «Consejos» si el módulo de análisis no
  // está.
  const moduloConsejos = useModuloConsejos();
  // «A bordo» solo tiene sentido si el presupuesto es de dos o más.
  const compartido = useTripulacion().length > 1;
  const noDisponibles = useMemo(() => {
    const set = new Set<IdWidget>();
    if (moduloConsejos === null) {
      set.add('consejos');
    }
    if (!compartido) {
      set.add('a-bordo');
    }
    return set;
  }, [moduloConsejos, compartido]);

  const {
    editando,
    entrarEnEdicion,
    salirDeEditar,
    visibles,
    abrirCatalogo,
    rejilla,
    capas,
  } = useEdicionTarjetas({ widgets, guardar, restablecer, noDisponibles });

  // Brújula de Inicio (antes, «Editar» y ⚙ en la cabecera): personalizar
  // los widgets. «Revisar N» no sale (B6): ya es botón de Por hacer.
  // «Sincronizar bancos» tampoco (decisión del 5-oct-2026): se sincroniza
  // deslizando hacia abajo en Cuentas. Inicio no cambia de mes.
  useOpcionesPantalla([
    {
      id: 'personalizar',
      tipo: 'propia',
      texto: editando ? t('Done editing home') : t('Customize home'),
      icono: 'grip',
      onPress: () => (editando ? salirDeEditar() : entrarEnEdicion()),
    },
    {
      id: 'preguntar-gavi',
      tipo: 'propia',
      texto: t('Ask Gavi'),
      icono: 'sparkle',
      onPress: () => void navigate(RUTA_GAVI),
      oculta: !gaviInteligente || editando,
    },
  ]);

  return (
    <Page
      header={
        <SaludoCabecera
          month={month}
          derecha={
            // El ojo de buey (ayuda de Gavi) siempre; a su lado, el ancla de
            // la copia de seguridad o, mientras se edita, «Hecho».
            <View
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}
            >
              <OjoBuey onPress={() => setAyudaAbierta(true)} />
              {editando ? (
                <Boton
                  onPress={salirDeEditar}
                  aria-label={t('Done editing home')}
                  data-testid="inicio-hecho"
                >
                  <Trans>Done</Trans>
                </Boton>
              ) : (
                <AnclaCopia sobreCielo />
              )}
            </View>
          }
        />
      }
      padding={0}
    >
      <SheetNameProvider name={sheetName}>
        <SyncRefresh
          onSync={async () => {
            await dispatch(sync());
          }}
        >
          {() => (
            <View style={{ flexShrink: 0 }}>
              {!editando && <AvisoLlegada />}
              {!initialized ? (
                <Cargando />
              ) : visibles.length === 0 && !editando ? (
                <View style={estilos.lista} data-testid="inicio-widgets">
                  <EstadoVacio
                    ilustracion="barquito"
                    titulo={t('Your home is empty')}
                    texto={t(
                      'Nothing on your home. Open Options → Customize home to add widgets.',
                    )}
                    accion={
                      <Boton onPress={entrarEnEdicion}>
                        <Trans>Customize home</Trans>
                      </Boton>
                    }
                  />
                </View>
              ) : (
                <View
                  className={claseLista}
                  style={{ ...estilos.lista, gap: espacio.tarjetas }}
                >
                  <RejillaInicio
                    {...rejilla}
                    pintar={(w, { tamano, media }) => {
                      const Widget = COMPONENTES_WIDGET[w.id];
                      return (
                        <Widget tamano={tamano} media={media} month={month} />
                      );
                    }}
                  />
                  {editando ? (
                    <BotonAnadirTarjeta onPress={abrirCatalogo} />
                  ) : (
                    <Button
                      variant="bare"
                      onPress={entrarEnEdicion}
                      data-testid="inicio-personalizar"
                      style={{
                        minHeight: 44,
                        alignSelf: 'center',
                        padding: '0 12px',
                        fontSize: 14,
                        fontWeight: 800,
                        color: color.accent,
                      }}
                    >
                      <Trans>Customize home</Trans>
                    </Button>
                  )}
                </View>
              )}
            </View>
          )}
        </SyncRefresh>
      </SheetNameProvider>
      {!editando && <AvisoPagaLlegada />}
      {capas}
      {ayudaAbierta && (
        <AyudaGavi
          pantalla={t('Home')}
          preguntas={preguntasAyuda}
          onCerrar={() => setAyudaAbierta(false)}
        />
      )}
      {/* Último hijo del elemento que desplaza (el `main` de Page): así el
          Resumen (o lo que vaya al final) se lee entero sobre la barra. */}
      <HuecoBarra />
    </Page>
  );
}
